import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { ICellRendererParams } from '@ag-grid-community/core';
import { FavoritesService } from '@products/services/favorites.service';
import { FundShareClassId, ProductData } from '@types';
import { ProfileService } from '@services/profile.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IUserProfile } from '@services/profile.interface';
import { SignInService } from '@services/sign-in.service';
import { TranslateService } from '@shared/translate/translate.service';
import { DOCUMENT } from '@angular/common';

export interface PpssTabComponent {
  updateFavourite: (fund: FundShareClassId) => void;
}

export interface FavFundCellsParams {
  data?: ProductData;
  parentComponent?: PpssTabComponent;
}
@Component({
  selector: 'fav-fund-cell-renderer',
  templateUrl: './fav-fund-cell-renderer.component.html',
  styleUrls: ['./fav-fund-cell-renderer.component.scss'],
})
export class FavFundCellRendererComponent implements OnDestroy {
  active: boolean;
  fund: string;
  isLoggedIn: boolean;
  tooltipContent: string;
  ppssTabComponent: PpssTabComponent;
  hasFavourite = false;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private favoritesService: FavoritesService,
    private profileService: ProfileService,
    private signInService: SignInService,
    private renderer: Renderer2,
    private translateService: TranslateService,
    @Inject(DOCUMENT) readonly documentRef: Document,
    private cdr: ChangeDetectorRef
  ) {
    this.hasFavourite = this.favoritesService.isFavouriteEnabled();
    this.subscribeToModalLogin();
  }

  agInit(params: FavFundCellsParams) {
    const product: ProductData = params.data;
    this.ppssTabComponent = params?.parentComponent;

    this.fund = `${product.fundId}-${product?.shareClass?.identifiers?.shareClassCode}`;
    this.active = product.isFavoriteFund;

    this.profileService
      .getUserProfile$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile: IUserProfile) => {
        this.isLoggedIn = profile?.isLoggedIn;
        this.setTooltipContent();
      });
  }

  setTooltipContent() {
    if (this.isLoggedIn) {
      if (this.active) {
        this.tooltipContent = this.translateService.instant(
          'products.remove-from-favourite'
        );
      } else {
        this.tooltipContent = this.translateService.instant(
          'products.add-to-favourite'
        );
      }
    } else {
      if (this.active) {
        this.tooltipContent = this.translateService.instant(
          'products.signin-to-remove-favourite'
        );
      } else {
        this.tooltipContent = this.translateService.instant(
          'products.signin-to-add-favourite'
        );
      }
    }
  }

  toggle() {
    // Disable Focus on cell-click
    if (this.documentRef.activeElement instanceof HTMLElement) {
      this.documentRef.activeElement.blur();
    }
    if (this.isLoggedIn) {
      this.active = !this.active;
      this.ppssTabComponent.updateFavourite(this.fund as FundShareClassId);
      this.cdr.detectChanges();
      this.favoritesService.toggleFavorite(this.fund as FundShareClassId);
    } else {
      this.signInService.showSignInModal();
      this.signInService.showFavMsgInModal();
    }
    this.setTooltipContent();
  }

  subscribeToModalLogin() {
    this.favoritesService
      .hideSignModalAfterLogin$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((resp) => {
        if (resp) {
          this.signInService.hideSignInModal();
        }
      });
  }

  onTooltipClicked(event) {
    // Stop bubbling to prevent click on parent
    event.stopPropagation();
  }

  ngOnDestroy() {
    this.renderer.destroy();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
